import { Form } from "react-bootstrap";
import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import { Input, PageWrapper, Select } from "../../components";
import { useTranslation } from "react-i18next";
import { Pages } from '../../shared/enums';
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getDriverTierDetail, createDriverTier, updateDriverTier } from "../../api/driver-tier";
import { RiEdit2Fill, RiAddLargeLine } from "react-icons/ri";
import Button from '../../components/common/Button';
import { Card } from "react-bootstrap";

const UPGRADE_RULE_TYPE_OPTIONS = [
    { value: 'order_count', labelKey: 'UPGRADE_RULE_ORDER_COUNT' },
    // { value: 'total_sales', labelKey: 'UPGRADE_RULE_TOTAL_SALES' },
    // { value: 'rating', labelKey: 'UPGRADE_RULE_RATING' },
];
const UPGRADE_RULE_PERIOD_OPTIONS = [
    { value: 'all_time', labelKey: 'UPGRADE_RULE_PERIOD_ALL_TIME' },
    { value: 'yearly', labelKey: 'UPGRADE_RULE_PERIOD_YEARLY' },
    { value: 'monthly', labelKey: 'UPGRADE_RULE_PERIOD_MONTHLY' },
];
const UPGRADE_RULE_OPERATOR_OPTIONS = [
    { value: 'gte', labelKey: 'UPGRADE_RULE_OPERATOR_GTE' },
    { value: 'lte', labelKey: 'UPGRADE_RULE_OPERATOR_LTE' },
    { value: 'eq', labelKey: 'UPGRADE_RULE_OPERATOR_EQ' },
];

const DriverTierDetail = () => {
    const { t } = useTranslation();
    const form = useForm({
        defaultValues: {
            tierName: '',
            driverCommissionPercentage: '',
            companyCommissionPercentage: '',
            description: '',
            upgradeRules: [],
        }
    });
    const { fields, append, remove } = useFieldArray({ control: form.control, name: 'upgradeRules' });
    const navigate = useNavigate();
    const { id } = useParams();
    const [isEdit, setIsEdit] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);

    useEffect(() => {
        const getData = async () => {
            const response = await getDriverTierDetail(id);
            form.reset({
                tierName: response.tierName,
                companyCommissionPercentage: response.companyCommissionPercentage,
                driverCommissionPercentage: response.driverCommissionPercentage,
                description: response.description || '',
                upgradeRules: (response.upgradeRules || []).map(r => ({
                    type: r.type,
                    period: r.period,
                    threshold: r.threshold,
                    operator: r.operator,
                })),
            });
        };

        if (id !== "new") {
            setIsEdit(true);
            getData();
        }
    }, [id, form]);

    const create = async (data) => {
        setBtnLoading(true);
        try {
            const response = await createDriverTier({
                tierName: data.tierName,
                driverCommissionPercentage: Number(data.driverCommissionPercentage),
                companyCommissionPercentage: Number(data.companyCommissionPercentage),
                description: data.description,
                upgradeRules: (data.upgradeRules || []).map(r => ({
                    type: r.type,
                    period: r.period,
                    threshold: Number(r.threshold),
                    operator: r.operator || 'gte',
                })),
            });
            if (response.success) navigate(Pages.DriverTier);
        } catch (error) {
            console.error('Error creating tier:', error);
        } finally {
            setBtnLoading(false);
        }
    };

    const update = async (data) => {
        setBtnLoading(true);
        try {
            const response = await updateDriverTier(id, {
                tierName: data.tierName,
                driverCommissionPercentage: Number(data.driverCommissionPercentage),
                companyCommissionPercentage: Number(data.companyCommissionPercentage),
                description: data.description,
                upgradeRules: (data.upgradeRules || []).map(r => ({
                    type: r.type,
                    period: r.period,
                    threshold: Number(r.threshold),
                    operator: r.operator || 'gte',
                })),
            });
            if (response.success) navigate(Pages.DriverTier);
        } catch (error) {
            console.error('Error updating tier:', error);
        } finally {
            setBtnLoading(false);
        }
    };

    const addRule = () => append({ type: "", period: "", threshold: null, operator: "" });

    const handleCompanyCommissionChange = (value) => {
        const companyCommission = Number(value) || 0;
        const driverCommission = 100 - companyCommission;
        form.setValue('driverCommissionPercentage', driverCommission);
    };

    return (
        <PageWrapper
            headerProps={{
                title: isEdit ? "EDIT_DRIVER_TIER" : "ADD_DRIVER_TIER",
                rightElement: (
                    <Button
                        type="submit"
                        variant="primary"
                        icon={isEdit ? <RiEdit2Fill /> : <RiAddLargeLine />}
                        loading={btnLoading}
                        onClick={() => form.handleSubmit(isEdit ? update : create)()}
                    >
                        {t(isEdit ? "UPDATE" : "CREATE")}
                    </Button>
                )
            }}
        >
            <FormProvider {...form}>
                <Form>
                    <Input
                        label={t("TIER_NAME")}
                        name="tierName"
                        rules={{ required: t("TIER_NAME_IS_REQUIRED") }}
                    />
                    <Input
                        label={t("COMPANY_COMMISSION_PERCENTAGE")}
                        name="companyCommissionPercentage"
                        type="number"
                        step="0.01"
                        rules={{
                            required: t("COMPANY_COMMISSION_IS_REQUIRED"),
                            min: { value: 0, message: t("COMMISSION_MIN_0") },
                            max: { value: 100, message: t("COMMISSION_MAX_100") }
                        }}
                        formControlProps={{
                            onChange: (e) => handleCompanyCommissionChange(e.target.value)
                        }}
                    />
                    <Input
                        label={t("DRIVER_COMMISSION_PERCENTAGE")}
                        name="driverCommissionPercentage"
                        type="number"
                        step="0.01"
                        formControlProps={{
                            disabled: true
                        }}
                        rules={{
                            required: t("DRIVER_COMMISSION_IS_REQUIRED")
                        }}
                    />
                    <Input
                        label={t("DESCRIPTION")}
                        name="description"
                        type="textarea"
                        rows="3"
                    />
                    <div className="mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <strong>{t("UPGRADE_RULES")}</strong>
                            <Button variant="outline-primary" size="sm" onClick={addRule}>
                                {t("ADD_RULE")}
                            </Button>
                        </div>
                        <p className="text-muted small mb-2">{t("UPGRADE_RULES_HINT")}</p>
                        {fields.map((field, index) => (
                            <Card key={field.id} className="mb-2">
                                <Card.Header className="py-2 d-flex align-items-center justify-content-between">
                                    <span>{t("RULE")} {index + 1}</span>
                                    <Button variant="danger" size="sm" onClick={() => remove(index)}>
                                        {t("REMOVE")}
                                    </Button>
                                </Card.Header>
                                <Card.Body className="py-2">
                                    <div className="row g-2">
                                        <div className="col-md-4">
                                            <Select
                                                label={t("RULE_TYPE")}
                                                name={`upgradeRules.${index}.type`}
                                                options={UPGRADE_RULE_TYPE_OPTIONS.map(o => ({ label: t(o.labelKey), value: o.value }))}
                                                rules={{ required: t("CANT_BE_EMPTY") }}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <Select
                                                label={t("RULE_PERIOD")}
                                                name={`upgradeRules.${index}.period`}
                                                options={UPGRADE_RULE_PERIOD_OPTIONS.map(o => ({ label: t(o.labelKey), value: o.value }))}
                                                rules={{ required: t("CANT_BE_EMPTY") }}
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <Input
                                                label={t("RULE_THRESHOLD")}
                                                name={`upgradeRules.${index}.threshold`}
                                                type="number"
                                                rules={{ required: t("CANT_BE_EMPTY"), min: { value: 0, message: t("MIN_0") } }}
                                                formControlProps={{ step: "0.01", min: 0 }}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <Select
                                                label={t("RULE_OPERATOR")}
                                                name={`upgradeRules.${index}.operator`}
                                                rules={{ required: t("CANT_BE_EMPTY") }}
                                                options={UPGRADE_RULE_OPERATOR_OPTIONS.map(o => ({ label: t(o.labelKey), value: o.value }))}
                                            />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        ))}
                        {fields.length === 0 && (
                            <p className="text-muted small">{t("NO_UPGRADE_RULES")}</p>
                        )}
                    </div>
                </Form>
            </FormProvider>
        </PageWrapper>
    );
};

export default DriverTierDetail;



